import React from "react";
import { TopBar } from "@/components/TopBar";
import { apiFormPos, apiUbahFormPos, formatApiErrorDetail } from "@/lib/api";
import { Loader2, Plus, Save, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Pengaturan → Form per POS (F1.5-J, owner only). Checklist items and the
// Press components per station; every save is a new version on the server
// (old versions are kept, steps already running keep their own copy).
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "rounded border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-amber-500/60";
const kunciDari = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40);

// owner 2026-10-12: the Manager reads the forms; only the owner changes them
function Lihat({ t }) {
  return (
    <div data-testid={`form-pos-lihat-${t.pos_code}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-2">
      <div className="text-xs text-slate-400">Versi aktif: {t.versi}. Hanya Owner yang bisa mengubah.</div>
      {t.butir.length === 0 ? <div className="text-sm text-slate-500">Belum ada butir checklist.</div> : (
        <ol className="list-decimal pl-5 space-y-1 text-sm text-slate-200">
          {t.butir.map((b) => <li key={b.kunci}>{b.label} <span className="text-xs text-slate-500">{b.tipe === "TEKS" ? "isian teks" : "centang"}{b.wajib ? " · wajib" : ""}{b.jenis ? ` · hanya ${b.jenis.join(", ")}` : ""}</span></li>)}
        </ol>)}
      {t.qty?.mode === "KOMPONEN" && <div className="text-xs text-slate-400">Komponen: {(t.qty.komponen || []).join(", ")}</div>}
    </div>
  );
}

function Editor({ t, onSaved }) {
  const [butir, setButir] = React.useState(t.butir.map((b) => ({ ...b })));
  const [komponen, setKomponen] = React.useState((t.qty?.komponen || []).join("\n"));
  const [baru, setBaru] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const ubah = (i, patch) => setButir((xs) => xs.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const geser = (i, d) => setButir((xs) => { const ys = [...xs]; const j = i + d; if (j < 0 || j >= ys.length) return xs; [ys[i], ys[j]] = [ys[j], ys[i]]; return ys; });
  const tambah = () => {
    const label = baru.trim();
    if (!label) return;
    let k = kunciDari(label) || "butir";
    let n = 2;
    while (butir.some((b) => b.kunci === k)) k = `${kunciDari(label).slice(0, 36)}_${n++}`;
    setButir([...butir, { kunci: k, label, wajib: true, tipe: "CENTANG" }]); setBaru("");
  };
  const simpan = async () => {
    setBusy(true); setMsg(null);
    const payload = { butir: butir.map(({ kunci, label, wajib, tipe, jenis }) => ({ kunci, label, wajib, tipe, ...(jenis ? { jenis } : {}) })) };
    if (t.qty?.mode === "KOMPONEN") payload.komponen = komponen.split("\n").map((x) => x.trim()).filter(Boolean);
    try { const r = await apiUbahFormPos(t.pos_code, payload); setMsg(`Tersimpan sebagai versi ${r.versi}`); onSaved(r); }
    catch (e) { setMsg(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid={`form-pos-editor-${t.pos_code}`} className="rounded-xl border border-slate-800 bg-slate-900/60 p-4 space-y-3">
      <div className="text-xs text-slate-400">Versi aktif: {t.versi}. Perubahan berlaku untuk pekerjaan yang <b>dimulai</b> sesudah disimpan.
        {t.qty && <> Qty: {t.qty.mode === "KOMPONEN" ? "per komponen (potong)" : `hasil + reject (${t.qty.satuan?.toLowerCase()})`}.</>}</div>
      <div className="space-y-1">
        {butir.length === 0 && <div className="text-sm text-slate-500">Belum ada butir checklist.</div>}
        {butir.map((b, i) => (
          <div key={b.kunci} className="flex flex-wrap items-center gap-2">
            <input value={b.label} onChange={(e) => ubah(i, { label: e.target.value })} className={`${inputCls} flex-1 min-w-[12rem]`} />
            <select value={b.tipe} onChange={(e) => ubah(i, { tipe: e.target.value })} className={inputCls}>
              <option value="CENTANG">Centang</option><option value="TEKS">Isian teks</option></select>
            <label className="flex items-center gap-1 text-xs text-slate-300"><input type="checkbox" checked={b.wajib} onChange={(e) => ubah(i, { wajib: e.target.checked })} /> wajib</label>
            {b.jenis && <span className="text-[11px] text-slate-500">hanya {b.jenis.join(", ")}</span>}
            <button onClick={() => geser(i, -1)} className="text-slate-500 hover:text-slate-200"><ArrowUp size={14} /></button>
            <button onClick={() => geser(i, 1)} className="text-slate-500 hover:text-slate-200"><ArrowDown size={14} /></button>
            <button onClick={() => setButir(butir.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-300"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="form-pos-baru" value={baru} onChange={(e) => setBaru(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); tambah(); } }}
          placeholder="Butir baru" className={`${inputCls} flex-1`} />
        <button onClick={tambah} className="inline-flex items-center gap-1 rounded border border-slate-600 px-3 text-sm text-slate-200"><Plus size={14} /> Tambah</button>
      </div>
      {t.qty?.mode === "KOMPONEN" && (
        <div><label className="text-xs text-slate-400">Komponen press (satu per baris)</label>
          <textarea data-testid="form-pos-komponen" rows={5} value={komponen} onChange={(e) => setKomponen(e.target.value)} className={`${inputCls} w-full mt-1`} /></div>
      )}
      <div className="flex items-center gap-3">
        <button data-testid="form-pos-simpan" disabled={busy} onClick={simpan} className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan versi baru</button>
        {msg && <span data-testid="form-pos-msg" className="text-sm text-slate-300">{msg}</span>}
      </div>
    </div>
  );
}

export default function FormPosPage() {
  const { user } = useAuth();
  const lihatSaja = user?.role === "manager";
  const [list, setList] = React.useState(null);
  const [pilih, setPilih] = React.useState(null);
  const [error, setError] = React.useState(null);
  React.useEffect(() => {
    apiFormPos().then((x) => { setList(x); setPilih(x[0]?.pos_code || null); }).catch((e) => setError(errText(e)));
  }, []);
  const t = list?.find((x) => x.pos_code === pilih);
  return (
    <div data-testid="form-pos-page" className="min-h-screen bg-slate-950 text-slate-200">
      <TopBar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-100">Form per POS</h1>
          <p className="text-sm text-slate-400 mt-1">Checklist yang diisi operator Jersey saat menyelesaikan pekerjaan di tiap pos, dan checklist ACC customer. Butir wajib yang tidak dicentang harus diberi alasan oleh operator.</p>
        </div>
        {error && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
        <div className="flex flex-wrap gap-2">
          {list?.map((x) => (
            <button key={x.pos_code} data-testid={`form-pos-tab-${x.pos_code}`} onClick={() => setPilih(x.pos_code)}
              className={`rounded-md px-3 py-1.5 text-sm border ${pilih === x.pos_code ? "border-amber-500 bg-amber-500 text-slate-950 font-semibold" : "border-slate-700 text-slate-400"}`}>{x.nama}</button>
          ))}
        </div>
        {t && (lihatSaja ? <Lihat key={t.pos_code} t={t} />
          : <Editor key={t.pos_code} t={t} onSaved={(r) => setList((xs) => xs.map((x) => (x.pos_code === r.pos_code ? r : x)))} />)}
      </main>
    </div>
  );
}
